package com.wintermar.inspection.core.data

import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

open class AuthorizationEncryption(
    private val secretKey: String
) {
    /**
     * hexChars holds the 16 hexadecimal digit characters (0–9, a–f). It's used as a lookup table so each 4-bit nibble of a byte maps directly to a character:
     * (v ushr 4) gives the high nibble (0..15) → index into hexChars for the first hex char.
     * (v and 0x0F) gives the low nibble (0..15) → index for the second hex char.
     * Lowercase a-f is a stylistic choice (uppercase A-F would work the same). This makes converting bytes to a hex string fast and simple.
     */
    private fun bufferToHex(buffer: ByteArray): String {
        val sb = StringBuilder(buffer.size * 2)
        val hexChars = "0123456789abcdef"
        for (b in buffer) {
            val v = b.toInt() and 0xFF
            sb.append(hexChars[v ushr 4])
            sb.append(hexChars[v and 0x0F])
        }
        return sb.toString()
    }

    @OptIn(ExperimentalTime::class)
    private fun formatTimestamp(instant: Instant): String {
        val dateTime = instant.toLocalDateTime(TimeZone.UTC)

        val year = dateTime.year
        val month = dateTime.month.ordinal + 1 // use month property
        val day = dateTime.day
        val hour = dateTime.hour
        val minute = dateTime.minute
        val second = dateTime.second
        val millis = (dateTime.nanosecond / 1_000_000)

        fun two(n: Int) = n.toString().padStart(2, '0')
        fun three(n: Int) = n.toString().padStart(3, '0')
        val sb = StringBuilder(17)
        sb.append(year.toString().padStart(4, '0'))
        sb.append(month.toString().padStart(2, '0'))
        sb.append(day.toString().padStart(2, '0'))
        sb.append(two(hour))
        sb.append(two(minute))
        sb.append(two(second))
        sb.append(three(millis))
        return sb.toString()
    }

    /**
     * Produce IV + AES-GCM(encryptedData) as hex string.
     * This is a suspend function because some platforms (JS) use async crypto APIs.
     */
    @OptIn(ExperimentalTime::class)
    open suspend fun getFinalEncrypt(token: String): String = withContext(Dispatchers.Default) {
        val timestamp = Clock.System.now()
        val formattedTimestamp = formatTimestamp(timestamp)
        val data = "${token.ifEmpty { "empty" }}:$formattedTimestamp"

        val initVector = Cryptography.generateRandomBytes(16)
        val encryptedData =
            Cryptography.encrypt(
                secretKey.encodeToByteArray(),
                initVector,
                data.encodeToByteArray()
            )

        bufferToHex(initVector) + bufferToHex(encryptedData)
    }
}
